/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * Bacheca avvisi dell'operatore: campanella in Dashboard + pannello di lettura.
 *
 * Perché esiste: chi torna sull'app dopo giorni non aveva modo di sapere cosa
 * fosse cambiato — una funzione nuova, una procedura diversa, una manutenzione
 * programmata. In un servizio a turni il passaparola non raggiunge chi non era
 * presente.
 *
 * Lo stato di lettura è sul SERVER, non in localStorage: lo stesso TAS2 usa il
 * tablet del PCA, il telefono e il PC di sede, e "già letto" deve valere su
 * tutti e tre.
 */
import { useState, useEffect, useCallback } from "react";
import {
  Bell, BellOff, X, Pin, Sparkles, ClipboardList, Wrench, Info, RefreshCw,
} from "lucide-react";
import { apiErr } from "@/lib/utils";
import { isOffline } from "../lib/connectivity";

/** Evento interno: fa ricontare i non letti ovunque sia mostrato il pallino. */
const EVT_AVVISI = "tas:avvisi";

export function segnalaAvvisiCambiati() {
  try { window.dispatchEvent(new CustomEvent(EVT_AVVISI)); } catch { /* ambiente senza window */ }
}

// ── Tipi: icona, etichetta e colore ──────────────────────────────────────────
const TIPI = {
  NOVITA:       { label: "Novità",       Icon: Sparkles,      cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  PROCEDURA:    { label: "Procedura",    Icon: ClipboardList, cls: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
  MANUTENZIONE: { label: "Manutenzione", Icon: Wrench,        cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  AVVISO:       { label: "Avviso",       Icon: Info,          cls: "text-slate-300 border-slate-600 bg-slate-700/40" },
};
export const TIPI_AVVISO = Object.entries(TIPI).map(([value, v]) => ({ value, label: v.label }));
const tipoOf = (t) => TIPI[t] || TIPI.AVVISO;

/** Data leggibile: "Oggi 14:30" per il giorno corrente, altrimenti data estesa. */
const quando = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const oggi = new Date();
  const ora = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === oggi.toDateString()) return `Oggi ${ora}`;
  const ieri = new Date(oggi); ieri.setDate(oggi.getDate() - 1);
  if (d.toDateString() === ieri.toDateString()) return `Ieri ${ora}`;
  return `${d.toLocaleDateString("it-IT", { day: "numeric", month: "long" })} · ${ora}`;
};

// ── Campanella ───────────────────────────────────────────────────────────────
/**
 * Pulsante con il pallino dei non letti. Il conteggio si ricarica al mount, al
 * ritorno in primo piano (un avviso può essere stato pubblicato mentre l'app era
 * chiusa) e quando qualcuno chiama segnalaAvvisiCambiati().
 */
export function CampanellaAvvisi({ api, onApri }) {
  const [nonLetti, setNonLetti] = useState(0);

  const carica = useCallback(() => {
    api.getAvvisiNonLetti()
      .then(({ data }) => setNonLetti(data?.non_letti || 0))
      .catch(() => { /* silenzioso: offline non è un errore da mostrare */ });
  }, [api]);

  useEffect(() => {
    carica();
    const suVisibilita = () => { if (document.visibilityState === "visible") carica(); };
    window.addEventListener(EVT_AVVISI, carica);
    document.addEventListener("visibilitychange", suVisibilita);
    return () => {
      window.removeEventListener(EVT_AVVISI, carica);
      document.removeEventListener("visibilitychange", suVisibilita);
    };
  }, [carica]);

  return (
    <button
      className="btn btn-secondary relative"
      onClick={onApri}
      data-testid="btn-avvisi"
      title={nonLetti ? `${nonLetti} avvis${nonLetti === 1 ? "o" : "i"} da leggere` : "Avvisi e novità"}
      aria-label={nonLetti ? `Avvisi: ${nonLetti} da leggere` : "Avvisi"}
    >
      <Bell size={16} />
      {nonLetti > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-yellow-500 text-black text-[10px] font-bold flex items-center justify-center"
          data-testid="badge-avvisi"
        >
          {nonLetti > 9 ? "9+" : nonLetti}
        </span>
      )}
    </button>
  );
}

// ── Pannello di lettura ──────────────────────────────────────────────────────
export function AvvisiModal({ api, onClose }) {
  const [avvisi, setAvvisi] = useState(null);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    let vivo = true;
    api.getAvvisi()
      .then(({ data }) => {
        if (!vivo) return;
        setAvvisi(data?.avvisi || []);
        // Segnati letti all'apertura: è il momento in cui l'operatore li vede
        // davvero. Se la chiamata fallisce (tipicamente senza rete) restano non
        // letti — è il comportamento prudente: meglio riproporli che perderli.
        if ((data?.non_letti || 0) > 0) {
          api.segnaAvvisiLetti()
            .then(() => segnalaAvvisiCambiati())
            .catch(() => {});
        }
      })
      .catch((err) => {
        if (!vivo) return;
        // Un messaggio unico per ogni causa rendeva impossibile capire cosa fosse
        // andato storto: "riprova quando c'è rete" con la rete presente manda a
        // cercare il problema dalla parte sbagliata. Si distingue la vera assenza
        // di rete dagli errori del server, e si riporta ciò che il server dice.
        const st = err?.response?.status;
        if (isOffline()) {
          setErrore("Sei offline: gli avvisi si leggono quando torna la rete.");
        } else if (!err?.response) {
          // Nessuna response con la rete presente: quasi sempre è un errore
          // interno del server. Le risposte 500 di questa API escono SENZA header
          // CORS (non c'è un exception handler globale), quindi il browser le
          // blocca e axios le presenta identiche a una caduta di rete. Non dire
          // "riprova quando c'è rete": manda a cercare il problema dalla parte
          // sbagliata.
          setErrore("Il server non ha risposto correttamente: probabile errore interno. Segnalalo all'amministratore indicando l'ora.");
        } else if (st === 404) {
          setErrore("Funzione non disponibile su questo server (404): il backend non è aggiornato. Segnalalo all'amministratore.");
        } else if (st === 401 || st === 403) {
          setErrore("Sessione non valida. Esci e rientra, poi riprova.");
        } else {
          setErrore(`Errore ${st || ""} nel caricamento degli avvisi. ${apiErr(err, "")}`.trim());
        }
      });
    return () => { vivo = false; };
  }, [api]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* `p-5`: `.tactical-card` definisce il padding solo sotto i 768px */}
      <div className="tactical-card w-full max-w-xl max-h-[85vh] flex flex-col p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">
              <Bell size={16} className="text-orange-400" /> Avvisi e novità
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Cosa è cambiato nell'app e le comunicazioni dello staff
            </p>
          </div>
          <button className="text-slate-400 hover:text-white" onClick={onClose} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>

        {/* Elenco scorrevole. `pr-2 -mr-2` tiene la barra di scroll fuori dal testo. */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2">
          {errore && (
            <div className="p-3 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200">
              {errore}
            </div>
          )}

          {!avvisi && !errore && (
            <div className="flex items-center justify-center h-32 text-slate-400">
              <RefreshCw size={18} className="animate-spin mr-2" /> Caricamento…
            </div>
          )}

          {avvisi && avvisi.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-slate-500 gap-2 text-center">
              <BellOff size={30} />
              <div className="text-sm">Nessun avviso.</div>
              <div className="text-xs max-w-sm">
                Qui compaiono le novità dell'app e le comunicazioni dello staff pubblicate
                da quando ti sei registrato.
              </div>
            </div>
          )}

          {avvisi && avvisi.map((a) => {
            const t = tipoOf(a.tipo);
            return (
              <div
                key={a.id}
                className={`tactical-card p-4 mb-2 ${a.letto ? "" : "border-orange-500/40 bg-orange-500/5"}`}
              >
                <div className="flex items-start gap-2 flex-wrap mb-1.5">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${t.cls}`}>
                    <t.Icon size={11} /> {t.label}
                  </span>
                  {a.fissato && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-600 bg-slate-700/40 text-[11px] text-slate-300">
                      <Pin size={11} /> In evidenza
                    </span>
                  )}
                  {!a.letto && (
                    <span className="px-2 py-0.5 rounded-full bg-yellow-500 text-black text-[11px] font-bold">
                      nuovo
                    </span>
                  )}
                  <span className="text-xs text-slate-500 ml-auto">{quando(a.created_at)}</span>
                </div>
                <div className="text-white font-medium text-sm">{a.titolo}</div>
                <div className="text-slate-300 text-sm mt-1 whitespace-pre-line leading-relaxed">
                  {a.corpo}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end border-t border-slate-700 pt-4 mt-4 flex-shrink-0">
          <button className="btn btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

export default AvvisiModal;
